import { isObject } from 'xe-utils';

const RSA_PUB_KEY = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDTNkE62qHn95/oE+c8mUhM4mLc
rhaOS4YnMEsK22NBu5DDYgyZBrOah3tjEp/HrVZD5krmwXgGd/MiS1TfmDRh5uEm
tRfZu9XB4kXSVkziFvNMBKMTqKhIeYkHBHn51fLo8rlt5rDHVrrRBXV+0dNHJoXY
ZipAA4xx9AusaT64jQIDAQAB
-----END PUBLIC KEY-----`;

// 将字符串转换为ArrayBuffer
function strToArrayBuffer(str) {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

// 将ArrayBuffer转换为Base64字符串
function arrayBufferToBase64(buffer) {
  let binary = '';
  let bytes = new Uint8Array(buffer);
  let len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
// 将Base64字符串转换为ArrayBuffer
function base64ToArrayBuffer(encrypted) {
  return new Uint8Array(
    atob(encrypted)
      .split('')
      .map(function (c) {
        return c.charCodeAt(0);
      }),
  );
}
// AES-CBC 加密
async function encryptAESCbc(data) {
  // 生成加密所需的密钥和初始向量
  const keyBuffer = window.crypto.getRandomValues(new Uint8Array(16)); // 128bit key
  const ivBuffer = keyBuffer.slice(0, 16);

  // 导入密钥
  const key = await window.crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-CBC' },
    false,
    ['encrypt'],
  );

  // 加密
  const dataBuffer = strToArrayBuffer(data);
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv: ivBuffer,
    },
    key,
    dataBuffer,
  );

  return {
    encrypted: arrayBufferToBase64(encryptedBuffer),
    key: arrayBufferToBase64(keyBuffer),
    iv: arrayBufferToBase64(ivBuffer),
  };
}

// 导入 RSA 公钥
async function importPublicKey(pem) {
  const cleanedPem = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '');

  const keyData = base64ToArrayBuffer(cleanedPem);

  return await crypto.subtle.importKey(
    'spki',
    keyData.buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['encrypt'],
  );
}

// RSA-OAEP 加密
async function encryptRSA(data, publicKey) {
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);
  return await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey,
    encodedData,
  );
}

export async function encryptMessage(message) {
  const { encrypted, key } = await encryptAESCbc(message);
  const publicKey = await importPublicKey(RSA_PUB_KEY);
  const encryptedKey = await encryptRSA(key, publicKey);

  return {
    encrypted,
    key: arrayBufferToBase64(encryptedKey),
  };
}
// 加密对象数据
export function encryptData(data, nonce, timestamp) {
  const dataArr = Object.entries(data)
    .filter(([, item]) => item && !isObject(item))
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, v]) => v);
  return encryptMessage(`${dataArr.join('')}${nonce}${timestamp}`);
}

const forge = require('node-forge');
// rsa解密
function rsaDecrypt(encrypted, privateKeyPem) {
  // 解析私钥
  const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);
  // 使用私钥进行解密
  return privateKey.decrypt(forge.util.decode64(encrypted), 'RSA-OAEP', {
    md: forge.md.sha256.create(),
  });
}
// aes 解密
function aesDecrypt(key, origData) {
  const k = forge.util.decode64(key);
  // 创建一个新的 forge buffer 使用 Base64 解码的密文
  const encryptedBytes = forge.util.decode64(origData);
  // 创建一个新的 forge buffer 使用 UTF-8 编码的 key
  const keyBytes = forge.util.createBuffer(k, 'utf8');
  // 创建一个新的 forge buffer 使用 UTF-8 编码的 iv
  const iv = forge.util.createBuffer(k, 'utf8');
  // 创建一个新的 decipher
  const decipher = forge.cipher.createDecipher('AES-CBC', keyBytes);
  // 设置 decipher 的初始向量
  decipher.start({ iv });
  // 更新 decipher 使用加密的 bytes
  decipher.update(forge.util.createBuffer(encryptedBytes));
  // 尝试完成解密过程
  const result = decipher.finish();
  // 检查解密是否成功
  if (!result) {
    throw new Error('Failed to decrypt ciphertext');
  }
  // 返回解密后的文本
  return decipher.output.toString();
}
// 数据解密
export async function decryptData(str, key) {
  const decryptedKey = rsaDecrypt(key, RSA_PUB_KEY);
  return await aesDecrypt(decryptedKey, str);
}

export function toUrlSafeBase64(str) {
  let base64 = btoa(String.fromCharCode(...new TextEncoder().encode(str)));
  base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return base64;
}